import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { ChevronRight, ChevronDown, Download, X } from 'lucide-react';
import type {
  OrgBillingRecord,
  DrillSortKey,
} from '../../../utils/orgBillingAnalysisUtils';
import {
  buildDrillDownByTechnology,
  buildDrillDownByGrade,
  buildDrillDownByProject,
  buildDrillDownByAccountOwnerHierarchy,
  sortTechEntries,
  sortResourceEntries,
  sortAccountOwnerAmEntries,
  sortAccountOwnerProjectEntries,
  rollupAccountOwnerAmNode,
  labelYearMonthKey,
  buildTimesheetDrillDownExportRows,
  buildTimesheetProjectMonthFirstExportRows,
  buildAccountOwnerHierarchyExportRows,
  groupResourcesByMonth,
  sortDrillMonthKeys,
  sortDrillMonthResourceRows,
  rollupMonthResourceRows,
  truncateWithEllipsis,
  type TimesheetDrillExportRow,
  type TimesheetAccountOwnerDrillExportRow,
  type TechDrillNode,
  type AccountOwnerDrillRootNode,
} from '../../../utils/orgBillingAnalysisUtils';
import { TechnologyBillingDrillScopeFilters } from './TechnologyBillingFilters';

export type TimesheetDrillVariant =
  | 'technology'
  | 'grade'
  | 'project'
  | 'accountOwner';

const ACCOUNT_OWNER_CSV_HEADERS: (keyof TimesheetAccountOwnerDrillExportRow)[] =
  [
    'Account owner',
    'Project',
    'Department',
    'User ID',
    'Resource',
    'Active',
    'Year-Month',
    'Month',
    'Billing Hours',
    'Non-Billing Hours',
    'Total Hours',
    'Leave Days',
    'Productivity %',
  ];

function downloadTimesheetDrillCsv(
  rows: TimesheetDrillExportRow[] | TimesheetAccountOwnerDrillExportRow[],
  variant: TimesheetDrillVariant,
) {
  if (rows.length === 0) return;
  const headers =
    variant === 'accountOwner'
      ? (ACCOUNT_OWNER_CSV_HEADERS as string[])
      : (Object.keys(rows[0]) as string[]);
  const esc = (v: unknown) => {
    const s = String(v ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const csv = [
    headers.join(','),
    ...rows.map((row) =>
      headers.map((h) =>
        esc((row as unknown as Record<string, unknown>)[h]),
      ).join(','),
    ),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const suffix =
    variant === 'grade'
      ? '_grade'
      : variant === 'project'
        ? '_project'
        : variant === 'accountOwner'
          ? '_account_owner'
          : '';
  a.download = `timesheet_insights_drilldown${suffix}_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function formatHours(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function prodClass(pct: number): string {
  if (pct >= 70) return 'text-emerald-600 font-semibold';
  if (pct >= 45) return 'text-amber-600 font-semibold';
  return 'text-red-500 font-semibold';
}

const MAX_VISIBLE_TECH_BADGES = 3;

const TECH_BADGE_PALETTE = [
  'bg-violet-100 text-violet-900 border-violet-200/90',
  'bg-sky-100 text-sky-900 border-sky-200/90',
  'bg-emerald-100 text-emerald-900 border-emerald-200/90',
  'bg-amber-100 text-amber-900 border-amber-200/90',
  'bg-rose-100 text-rose-900 border-rose-200/90',
  'bg-indigo-100 text-indigo-900 border-indigo-200/90',
  'bg-cyan-100 text-cyan-900 border-cyan-200/90',
  'bg-fuchsia-100 text-fuchsia-900 border-fuchsia-200/90',
  'bg-teal-100 text-teal-900 border-teal-200/90',
  'bg-orange-100 text-orange-900 border-orange-200/90',
  'bg-lime-100 text-lime-900 border-lime-200/90',
  'bg-pink-100 text-pink-900 border-pink-200/90',
] as const;

function techBadgePaletteIndex(label: string): number {
  const s = label.trim() || '—';
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % TECH_BADGE_PALETTE.length;
}

function techBadgeClassFor(label: string): string {
  const base =
    'inline-flex max-w-[9rem] truncate rounded-md text-[10px] font-medium px-1.5 py-0.5 border';
  return `${base} ${TECH_BADGE_PALETTE[techBadgePaletteIndex(label)]}`;
}

function TechBadgesCell({
  labels,
  modalTitle,
  onOpenModal,
}: {
  labels: string[] | undefined;
  modalTitle: string;
  onOpenModal: (title: string, all: string[]) => void;
}) {
  const list = labels?.length ? labels : [];
  if (list.length === 0) {
    return <span className="text-slate-400">—</span>;
  }
  const visible = list.slice(0, MAX_VISIBLE_TECH_BADGES);
  const extra = list.length - visible.length;
  const dialogTitle = `Technologies — ${modalTitle}`;
  return (
    <div className="flex flex-wrap items-center gap-1 min-w-0 max-w-[14rem]">
      {visible.map((t) => (
        <span key={t} className={techBadgeClassFor(t)} title={t}>
          {t}
        </span>
      ))}
      {extra > 0 ? (
        <button
          type="button"
          className="text-[10px] font-semibold text-blue-600 hover:text-blue-800 hover:underline shrink-0"
          onClick={() => onOpenModal(dialogTitle, list)}
        >
          +{extra}
        </button>
      ) : null}
    </div>
  );
}

interface ThProps {
  label: string;
  sortKey?: DrillSortKey;
  activeSortKey: DrillSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (key: DrillSortKey) => void;
  align?: 'left' | 'right';
}

const Th: React.FC<ThProps> = ({
  label,
  sortKey,
  activeSortKey,
  sortDir,
  onSort,
  align = 'left',
}) => {
  const active = sortKey && activeSortKey === sortKey;
  return (
    <th
      className={`
        px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap
        ${align === 'right' ? 'text-right' : 'text-left'}
        ${sortKey ? 'cursor-pointer select-none hover:text-slate-700' : ''}
      `}
      onClick={() => sortKey && onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (
          <span className="text-blue-500">{sortDir === 'asc' ? '↑' : '↓'}</span>
        )}
      </span>
    </th>
  );
}

export interface TechnologyBillingDrillDownTableProps {
  variant?: TimesheetDrillVariant;
  filteredRecords: OrgBillingRecord[];
  selectedTechnologies: string[];
  onSelectedTechnologiesChange: (next: string[]) => void;
  technologyOptions: string[];
  /** Project / account-owner tab: max visible chars for project name before …. */
  truncateSliceNameAt?: number;
  /** Project tab: multi-select technologies that scope which projects appear. */
  projectTechnologyFilterSelected?: string[];
  onProjectTechnologyFilterChange?: (next: string[]) => void;
  projectTechnologyFilterOptions?: string[];
}

/**
 * Expandable hierarchy + sort — same interaction model as Account Owner
 * {@link DrillDownTable}.
 */
export function TechnologyBillingDrillDownTable({
  variant = 'technology',
  filteredRecords,
  selectedTechnologies,
  onSelectedTechnologiesChange,
  technologyOptions,
  truncateSliceNameAt,
  projectTechnologyFilterSelected,
  onProjectTechnologyFilterChange,
  projectTechnologyFilterOptions,
}: TechnologyBillingDrillDownTableProps) {
  const projectTechColumn = variant === 'project';
  const projectMonthFirst = variant === 'project';
  const accountOwnerVariant = variant === 'accountOwner';

  const drillDown = useMemo((): Record<string, TechDrillNode> => {
    if (accountOwnerVariant) return {};
    if (variant === 'grade')
      return buildDrillDownByGrade(filteredRecords);
    if (variant === 'project')
      return buildDrillDownByProject(filteredRecords);
    return buildDrillDownByTechnology(filteredRecords);
  }, [variant, accountOwnerVariant, filteredRecords]);

  const drillDownAm = useMemo((): Record<string, AccountOwnerDrillRootNode> => {
    if (!accountOwnerVariant) return {};
    return buildDrillDownByAccountOwnerHierarchy(filteredRecords);
  }, [accountOwnerVariant, filteredRecords]);

  const [sortKey, setSortKey] = useState<DrillSortKey>('billable');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expandedTechs, setExpandedTechs] = useState<string[]>([]);
  const [expandedResources, setExpandedResources] = useState<string[]>([]);
  const [expandedAmProjects, setExpandedAmProjects] = useState<string[]>([]);
  const [expandedAmMonths, setExpandedAmMonths] = useState<string[]>([]);
  const [expandedProjectMonths, setExpandedProjectMonths] = useState<string[]>(
    [],
  );

  useEffect(() => {
    setExpandedTechs([]);
    setExpandedResources([]);
    setExpandedAmProjects([]);
    setExpandedAmMonths([]);
    setExpandedProjectMonths([]);
  }, [variant]);
  const [techBadgeModal, setTechBadgeModal] = useState<{
    title: string;
    labels: string[];
  } | null>(null);

  useEffect(() => {
    if (!techBadgeModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTechBadgeModal(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [techBadgeModal]);

  const handleSort = useCallback((key: DrillSortKey) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir(key === 'name' ? 'asc' : 'desc');
      return key;
    });
  }, []);

  const sortedTechEntries = useMemo(() => {
    const entries = Object.entries(drillDown) as [string, TechDrillNode][];
    const sorted = sortTechEntries(
      entries.map(([t, n]) => [t, { resources: n.resources }]),
      sortKey,
      sortDir,
    );
    return sorted.map(
      ([t]) => [t, drillDown[t]] as [string, TechDrillNode],
    );
  }, [drillDown, sortKey, sortDir]);

  const sortedAmEntries = useMemo(() => {
    return sortAccountOwnerAmEntries(
      Object.entries(drillDownAm),
      sortKey,
      sortDir,
    );
  }, [drillDownAm, sortKey, sortDir]);

  const exportRows = useMemo(() => {
    if (accountOwnerVariant)
      return buildAccountOwnerHierarchyExportRows(drillDownAm);
    if (projectMonthFirst)
      return buildTimesheetProjectMonthFirstExportRows(drillDown);
    return buildTimesheetDrillDownExportRows(drillDown);
  }, [accountOwnerVariant, projectMonthFirst, drillDownAm, drillDown]);
  const exportDisabled = exportRows.length === 0;

  const toggleTech = (tech: string) => {
    setExpandedTechs((prev) =>
      prev.includes(tech) ? prev.filter((x) => x !== tech) : [...prev, tech],
    );
  };

  const toggleResource = (tech: string, uid: string) => {
    const key = `${tech}::${uid}`;
    setExpandedResources((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key],
    );
  };

  const projectKeyAm = (am: string, projKey: string) => `${am}::${projKey}`;

  const toggleAmProject = (am: string, projKey: string) => {
    const key = projectKeyAm(am, projKey);
    setExpandedAmProjects((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key],
    );
  };

  const amMonthKey = (am: string, projKey: string, ym: string) =>
    `${am}::${projKey}::${ym}`;

  const toggleAmMonth = (am: string, projKey: string, ym: string) => {
    const key = amMonthKey(am, projKey, ym);
    setExpandedAmMonths((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key],
    );
  };

  const projectMonthExpandKey = (sliceKey: string, ym: string) =>
    `${sliceKey}::${ym}`;

  const toggleProjectMonth = (sliceKey: string, ym: string) => {
    const key = projectMonthExpandKey(sliceKey, ym);
    setExpandedProjectMonths((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key],
    );
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden relative">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-b border-slate-200">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
            Drill-down
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5 leading-none truncate">
            {variant === 'grade'
              ? 'Grade → resource → month'
              : variant === 'project'
                ? 'Project → month → resource'
                : variant === 'accountOwner'
                  ? 'Account owner → project → month → resource'
                  : 'Technology → resource → month'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <TechnologyBillingDrillScopeFilters
            filterLabel={
              variant === 'grade'
                ? 'Grade'
                : variant === 'project'
                  ? 'Project'
                  : variant === 'accountOwner'
                    ? 'Account owner'
                    : 'Tech'
            }
            selectedTechnologies={selectedTechnologies}
            onSelectedTechnologiesChange={onSelectedTechnologiesChange}
            technologyOptions={technologyOptions}
            enableOptionSearch={
              variant === 'project' || variant === 'accountOwner'
            }
            searchPlaceholder={
              variant === 'accountOwner'
                ? 'Search account owners…'
                : 'Search projects…'
            }
            emptySelectionSummary={
              variant === 'grade'
                ? 'All grades'
                : variant === 'project'
                  ? 'All projects'
                  : variant === 'accountOwner'
                    ? 'All account owners'
                    : 'All technologies'
            }
          />
          {variant === 'project' &&
          projectTechnologyFilterOptions != null &&
          onProjectTechnologyFilterChange != null &&
          projectTechnologyFilterSelected != null ? (
            <TechnologyBillingDrillScopeFilters
              filterLabel="Technologies"
              selectedTechnologies={projectTechnologyFilterSelected}
              onSelectedTechnologiesChange={onProjectTechnologyFilterChange}
              technologyOptions={projectTechnologyFilterOptions}
              enableOptionSearch
              searchPlaceholder="Search technologies…"
              emptySelectionSummary="All technologies"
            />
          ) : null}
          <div className="h-4 w-px bg-slate-200 flex-none hidden sm:block" />
          <button
            type="button"
            onClick={() => downloadTimesheetDrillCsv(exportRows, variant)}
            disabled={exportDisabled}
            className="flex items-center gap-1.5 shrink-0 px-3 py-1.5 text-[11px] font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Download drill-down as CSV (current filters)"
          >
            <Download size={13} />
            Export
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px] text-left">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/90">
              <th className="w-10 px-2 py-2.5" aria-hidden />
              <Th
                label="Name"
                sortKey="name"
                activeSortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
              />
              {projectTechColumn ? (
                <th
                  className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap text-left"
                  scope="col"
                >
                  Technologies
                </th>
              ) : null}
              <Th
                label={accountOwnerVariant ? 'Projects' : 'Employees'}
                sortKey="resources"
                activeSortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
              />
              <Th
                label="Billable"
                sortKey="billable"
                activeSortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
              />
              <Th
                label="Non-bill."
                sortKey="nonBillable"
                activeSortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
              />
              <Th
                label="Total"
                sortKey="total"
                activeSortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
              />
              <Th
                label="Leave"
                sortKey="leaveDays"
                activeSortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
              />
              <Th
                label="Prod. %"
                sortKey="productivity"
                activeSortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
              />
            </tr>
          </thead>
          <tbody>
            {accountOwnerVariant
              ? sortedAmEntries.map(([am, amNode]) => {
                  const roll = rollupAccountOwnerAmNode(amNode);
                  const amOpen = expandedTechs.includes(am);
                  const sortedProjects = sortAccountOwnerProjectEntries(
                    Object.entries(amNode.projects || {}),
                    sortKey,
                    sortDir,
                  );
                  return (
                    <React.Fragment key={am}>
                      <tr className="border-b border-slate-100 bg-slate-50/80">
                        <td className="px-2 py-2 align-top">
                          <button
                            type="button"
                            onClick={() => toggleTech(am)}
                            className="p-1 rounded-md hover:bg-slate-200/80 text-slate-600 mt-0.5"
                            aria-expanded={amOpen}
                          >
                            {amOpen ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                        </td>
                        <td className="px-3 py-2 min-w-0">
                          <div className="font-semibold text-slate-800">{am}</div>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-800 font-semibold align-top">
                          {roll.projectCount}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700 align-top">
                          {formatHours(roll.billable)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600 align-top">
                          {formatHours(roll.total - roll.billable)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-800 align-top">
                          {formatHours(roll.total)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-sky-700 align-top">
                          {roll.leaveDays.toFixed(1)}
                        </td>
                        <td
                          className={`px-3 py-2 text-right tabular-nums align-top ${prodClass(roll.productivity)}`}
                        >
                          {roll.productivity.toFixed(1)}%
                        </td>
                      </tr>
                      {amOpen
                        ? sortedProjects.map(([projKey, p]) => {
                            const resEntries = sortResourceEntries(
                              Object.entries(p.resources || {}),
                              sortKey,
                              sortDir,
                            );
                            const tb = resEntries.reduce(
                              (s, [, r]) => s + r.billable,
                              0,
                            );
                            const tt = resEntries.reduce(
                              (s, [, r]) => s + r.total,
                              0,
                            );
                            const tp = tt > 0 ? (tb / tt) * 100 : 0;
                            const pLeave = resEntries.reduce(
                              (s, [, r]) => s + (r.leaveDays || 0),
                              0,
                            );
                            const pk = projectKeyAm(am, projKey);
                            const projOpen = expandedAmProjects.includes(pk);
                            return (
                              <React.Fragment key={pk}>
                                <tr className="border-b border-slate-50 bg-white">
                                  <td className="px-2 py-1.5 pl-5">
                                    <button
                                      type="button"
                                      onClick={() => toggleAmProject(am, projKey)}
                                      className="p-1 rounded-md hover:bg-slate-100 text-slate-500"
                                      aria-expanded={projOpen}
                                    >
                                      {projOpen ? (
                                        <ChevronDown className="h-3.5 w-3.5" />
                                      ) : (
                                        <ChevronRight className="h-3.5 w-3.5" />
                                      )}
                                    </button>
                                  </td>
                                  <td className="px-3 py-1.5 text-slate-700 min-w-0">
                                    <div
                                      className="font-medium text-slate-800"
                                      title={p.displayName}
                                    >
                                      {truncateSliceNameAt != null
                                        ? truncateWithEllipsis(
                                            p.displayName,
                                            truncateSliceNameAt,
                                          )
                                        : p.displayName}
                                    </div>
                                    {p.clientAndPmSubtitle ? (
                                      <p
                                        className="text-[11px] text-slate-600 font-normal mt-0.5 leading-snug"
                                        title={p.clientAndPmSubtitle}
                                      >
                                        {p.clientAndPmSubtitle}
                                      </p>
                                    ) : null}
                                  </td>
                                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-800 font-semibold">
                                    {resEntries.length}
                                  </td>
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {formatHours(tb)}
                                  </td>
                                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                                    {formatHours(tt - tb)}
                                  </td>
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {formatHours(tt)}
                                  </td>
                                  <td className="px-3 py-1.5 text-right tabular-nums text-sky-700">
                                    {pLeave.toFixed(1)}
                                  </td>
                                  <td
                                    className={`px-3 py-1.5 text-right tabular-nums ${prodClass(tp)}`}
                                  >
                                    {tp.toFixed(1)}%
                                  </td>
                                </tr>
                                {projOpen
                                  ? (() => {
                                      const byMonth = groupResourcesByMonth(
                                        p.resources || {},
                                      );
                                      const monthKeys = sortDrillMonthKeys(
                                        byMonth,
                                        sortKey,
                                        sortDir,
                                      );
                                      return monthKeys.map((ym) => {
                                        const monthRows = byMonth[ym] || [];
                                        const mr =
                                          rollupMonthResourceRows(monthRows);
                                        const mk = amMonthKey(am, projKey, ym);
                                        const mo = expandedAmMonths.includes(
                                          mk,
                                        );
                                        const sortedUnder =
                                          sortDrillMonthResourceRows(
                                            monthRows,
                                            sortKey,
                                            sortDir,
                                          );
                                        const monthLabel =
                                          ym === ''
                                            ? '—'
                                            : labelYearMonthKey(ym);
                                        return (
                                          <React.Fragment key={mk}>
                                            <tr className="border-b border-slate-50 bg-white">
                                              <td className="px-2 py-1.5 pl-8">
                                                {sortedUnder.length > 0 ? (
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      toggleAmMonth(
                                                        am,
                                                        projKey,
                                                        ym,
                                                      )
                                                    }
                                                    className="p-1 rounded-md hover:bg-slate-100 text-slate-500"
                                                    aria-expanded={mo}
                                                  >
                                                    {mo ? (
                                                      <ChevronDown className="h-3.5 w-3.5" />
                                                    ) : (
                                                      <ChevronRight className="h-3.5 w-3.5" />
                                                    )}
                                                  </button>
                                                ) : null}
                                              </td>
                                              <td className="px-3 py-1.5 text-slate-600 min-w-0 italic text-[12px]">
                                                {monthLabel}
                                              </td>
                                              <td className="px-3 py-1.5 text-right tabular-nums text-slate-800 font-semibold">
                                                {mr.headcount}
                                              </td>
                                              <td className="px-3 py-1.5 text-right tabular-nums">
                                                {formatHours(mr.billable)}
                                              </td>
                                              <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                                                {formatHours(
                                                  mr.total - mr.billable,
                                                )}
                                              </td>
                                              <td className="px-3 py-1.5 text-right tabular-nums">
                                                {formatHours(mr.total)}
                                              </td>
                                              <td className="px-3 py-1.5 text-right tabular-nums text-sky-700">
                                                {mr.leaveDays.toFixed(1)}
                                              </td>
                                              <td
                                                className={`px-3 py-1.5 text-right tabular-nums ${prodClass(mr.productivity)}`}
                                              >
                                                {mr.productivity.toFixed(1)}%
                                              </td>
                                            </tr>
                                            {mo
                                              ? sortedUnder.map((row) => {
                                                  const b = row.bucket;
                                                  const rp =
                                                    b.total > 0
                                                      ? (b.billable / b.total) *
                                                        100
                                                      : 0;
                                                  return (
                                                    <tr
                                                      key={`${mk}::${row.uid}`}
                                                      className="border-b border-slate-50/60 bg-slate-50/50 text-[12px]"
                                                    >
                                                      <td />
                                                      <td className="px-3 py-0.5 pl-11 text-slate-700 min-w-0">
                                                        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0">
                                                          <span>{row.name}</span>
                                                          {row.isActive ? (
                                                            <span className="text-[10px] font-medium uppercase tracking-wide text-emerald-600">
                                                              active
                                                            </span>
                                                          ) : null}
                                                        </div>
                                                      </td>
                                                      <td className="px-3 py-0.5" aria-hidden />
                                                      <td className="px-3 py-0.5 text-right tabular-nums text-slate-700">
                                                        {b.billable.toLocaleString(
                                                          undefined,
                                                          {
                                                            maximumFractionDigits: 2,
                                                          },
                                                        )}
                                                      </td>
                                                      <td className="px-3 py-0.5 text-right tabular-nums text-slate-600">
                                                        {b.nonBillable.toLocaleString(
                                                          undefined,
                                                          {
                                                            maximumFractionDigits: 2,
                                                          },
                                                        )}
                                                      </td>
                                                      <td className="px-3 py-0.5 text-right tabular-nums text-slate-800">
                                                        {b.total.toLocaleString(
                                                          undefined,
                                                          {
                                                            maximumFractionDigits: 2,
                                                          },
                                                        )}
                                                      </td>
                                                      <td className="px-3 py-0.5 text-right tabular-nums text-sky-700">
                                                        {(
                                                          b.leaveDays || 0
                                                        ).toFixed(1)}
                                                      </td>
                                                      <td
                                                        className={`px-3 py-0.5 text-right tabular-nums ${prodClass(rp)}`}
                                                      >
                                                        {rp.toFixed(1)}%
                                                      </td>
                                                    </tr>
                                                  );
                                                })
                                              : null}
                                          </React.Fragment>
                                        );
                                      });
                                    })()
                                  : null}
                              </React.Fragment>
                            );
                          })
                        : null}
                    </React.Fragment>
                  );
                })
              : sortedTechEntries.map(([tech, node]) => {
              const resEntries = sortResourceEntries(
                Object.entries(node.resources || {}),
                sortKey,
                sortDir,
              );
              const tb = resEntries.reduce((s, [, r]) => s + r.billable, 0);
              const tt = resEntries.reduce((s, [, r]) => s + r.total, 0);
              const tp = tt > 0 ? (tb / tt) * 100 : 0;
              const techLeave = resEntries.reduce(
                (s, [, r]) => s + (r.leaveDays || 0),
                0,
              );
              const techOpen = expandedTechs.includes(tech);
              const sliceDisplayName =
                projectTechColumn && truncateSliceNameAt != null
                  ? truncateWithEllipsis(tech, truncateSliceNameAt)
                  : tech;
              const sliceTitle =
                projectTechColumn
                  ? [
                      tech,
                      node.sliceCategorySubtitle || null,
                      node.sliceProjectId != null
                        ? `ID ${node.sliceProjectId}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : undefined;
              return (
                <React.Fragment key={tech}>
                  <tr className="border-b border-slate-100 bg-slate-50/80">
                    <td className="px-2 py-2 align-top">
                      <button
                        type="button"
                        onClick={() => toggleTech(tech)}
                        className="p-1 rounded-md hover:bg-slate-200/80 text-slate-600 mt-0.5"
                        aria-expanded={techOpen}
                      >
                        {techOpen ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-2 min-w-0">
                      <div
                        className="font-semibold text-slate-800"
                        title={sliceTitle}
                        data-project-id={
                          projectTechColumn && node.sliceProjectId != null
                            ? node.sliceProjectId
                            : undefined
                        }
                      >
                        {sliceDisplayName}
                      </div>
                      {projectTechColumn && node.sliceCategorySubtitle ? (
                        <p
                          className="text-[11px] text-slate-500 font-normal mt-1 leading-snug"
                          title={node.sliceCategorySubtitle}
                        >
                          {node.sliceCategorySubtitle}
                        </p>
                      ) : null}
                      {!projectTechColumn && node.departmentSubtitle ? (
                        <p
                          className="text-[11px] text-slate-500 font-normal mt-1 leading-snug"
                          title={node.departmentSubtitle}
                        >
                          {node.departmentSubtitle}
                        </p>
                      ) : null}
                    </td>
                    {projectTechColumn ? (
                      <td className="px-3 py-2 align-top min-w-0">
                        <TechBadgesCell
                          labels={node.sliceTechnologiesRanked}
                          modalTitle={tech}
                          onOpenModal={(title, labels) =>
                            setTechBadgeModal({ title, labels })
                          }
                        />
                      </td>
                    ) : null}
                    <td className="px-3 py-2 text-right tabular-nums text-slate-800 font-semibold align-top">
                      {resEntries.length}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700 align-top">
                      {formatHours(tb)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600 align-top">
                      {formatHours(tt - tb)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-800 align-top">
                      {formatHours(tt)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-sky-700 align-top">
                      {techLeave.toFixed(1)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums align-top ${prodClass(tp)}`}
                    >
                      {tp.toFixed(1)}%
                    </td>
                  </tr>
                  {techOpen
                    ? projectMonthFirst
                      ? (() => {
                          const byMonth = groupResourcesByMonth(
                            node.resources || {},
                          );
                          const monthKeys = sortDrillMonthKeys(
                            byMonth,
                            sortKey,
                            sortDir,
                          );
                          return monthKeys.map((ym) => {
                            const monthRows = byMonth[ym] || [];
                            const mr = rollupMonthResourceRows(monthRows);
                            const mk = projectMonthExpandKey(tech, ym);
                            const mo = expandedProjectMonths.includes(mk);
                            const sortedUnder = sortDrillMonthResourceRows(
                              monthRows,
                              sortKey,
                              sortDir,
                            );
                            const monthLabel =
                              ym === '' ? '—' : labelYearMonthKey(ym);
                            return (
                              <React.Fragment key={mk}>
                                <tr className="border-b border-slate-50 bg-white">
                                  <td className="px-2 py-1.5 pl-5">
                                    {sortedUnder.length > 0 ? (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          toggleProjectMonth(tech, ym)
                                        }
                                        className="p-1 rounded-md hover:bg-slate-100 text-slate-500"
                                        aria-expanded={mo}
                                      >
                                        {mo ? (
                                          <ChevronDown className="h-3.5 w-3.5" />
                                        ) : (
                                          <ChevronRight className="h-3.5 w-3.5" />
                                        )}
                                      </button>
                                    ) : null}
                                  </td>
                                  <td className="px-3 py-1.5 text-slate-600 min-w-0 italic text-[12px]">
                                    {monthLabel}
                                  </td>
                                  {projectTechColumn ? (
                                    <td className="px-3 py-1.5" aria-hidden />
                                  ) : null}
                                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-800 font-semibold">
                                    {mr.headcount}
                                  </td>
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {formatHours(mr.billable)}
                                  </td>
                                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                                    {formatHours(mr.total - mr.billable)}
                                  </td>
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {formatHours(mr.total)}
                                  </td>
                                  <td className="px-3 py-1.5 text-right tabular-nums text-sky-700">
                                    {mr.leaveDays.toFixed(1)}
                                  </td>
                                  <td
                                    className={`px-3 py-1.5 text-right tabular-nums ${prodClass(mr.productivity)}`}
                                  >
                                    {mr.productivity.toFixed(1)}%
                                  </td>
                                </tr>
                                {mo
                                  ? sortedUnder.map((row) => {
                                      const b = row.bucket;
                                      const rp =
                                        b.total > 0
                                          ? (b.billable / b.total) * 100
                                          : 0;
                                      return (
                                        <tr
                                          key={`${mk}::${row.uid}`}
                                          className="border-b border-slate-50/60 bg-slate-50/50 text-[12px]"
                                        >
                                          <td />
                                          <td className="px-3 py-0.5 pl-10 text-slate-700 min-w-0">
                                            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0">
                                              <span>{row.name}</span>
                                              {row.isActive ? (
                                                <span className="text-[10px] font-medium uppercase tracking-wide text-emerald-600">
                                                  active
                                                </span>
                                              ) : null}
                                            </div>
                                          </td>
                                          {projectTechColumn ? (
                                            <td className="px-3 py-0.5 align-top min-w-0">
                                              <TechBadgesCell
                                                labels={row.technologiesRanked}
                                                modalTitle={row.name}
                                                onOpenModal={(title, labels) =>
                                                  setTechBadgeModal({
                                                    title,
                                                    labels,
                                                  })
                                                }
                                              />
                                            </td>
                                          ) : null}
                                          <td className="px-3 py-0.5" aria-hidden />
                                          <td className="px-3 py-0.5 text-right tabular-nums text-slate-700">
                                            {b.billable.toLocaleString(
                                              undefined,
                                              {
                                                maximumFractionDigits: 2,
                                              },
                                            )}
                                          </td>
                                          <td className="px-3 py-0.5 text-right tabular-nums text-slate-600">
                                            {b.nonBillable.toLocaleString(
                                              undefined,
                                              {
                                                maximumFractionDigits: 2,
                                              },
                                            )}
                                          </td>
                                          <td className="px-3 py-0.5 text-right tabular-nums text-slate-800">
                                            {b.total.toLocaleString(undefined, {
                                              maximumFractionDigits: 2,
                                            })}
                                          </td>
                                          <td className="px-3 py-0.5 text-right tabular-nums text-sky-700">
                                            {(b.leaveDays || 0).toFixed(1)}
                                          </td>
                                          <td
                                            className={`px-3 py-0.5 text-right tabular-nums ${prodClass(rp)}`}
                                          >
                                            {rp.toFixed(1)}%
                                          </td>
                                        </tr>
                                      );
                                    })
                                  : null}
                              </React.Fragment>
                            );
                          });
                        })()
                      : resEntries.map(([uid, r]) => {
                          const rk = `${tech}::${uid}`;
                          const ro = expandedResources.includes(rk);
                          const rp =
                            r.total > 0 ? (r.billable / r.total) * 100 : 0;
                          const monthKeys = Object.keys(r.byMonth || {}).sort();
                          return (
                            <React.Fragment key={rk}>
                              <tr className="border-b border-slate-50 bg-white">
                                <td className="px-2 py-1.5 pl-5">
                                  {monthKeys.length > 0 ? (
                                    <button
                                      type="button"
                                      onClick={() => toggleResource(tech, uid)}
                                      className="p-1 rounded-md hover:bg-slate-100 text-slate-500"
                                      aria-expanded={ro}
                                    >
                                      {ro ? (
                                        <ChevronDown className="h-3.5 w-3.5" />
                                      ) : (
                                        <ChevronRight className="h-3.5 w-3.5" />
                                      )}
                                    </button>
                                  ) : null}
                                </td>
                                <td className="px-3 py-1.5 text-slate-600 min-w-0">
                                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0">
                                    <span>{r.name}</span>
                                    {r.isActive ? (
                                      <span className="text-[10px] font-medium uppercase tracking-wide text-emerald-600">
                                        active
                                      </span>
                                    ) : null}
                                  </div>
                                </td>
                                {projectTechColumn ? (
                                  <td className="px-3 py-1.5 align-top min-w-0">
                                    <TechBadgesCell
                                      labels={r.technologiesRanked}
                                      modalTitle={r.name}
                                      onOpenModal={(title, labels) =>
                                        setTechBadgeModal({ title, labels })
                                      }
                                    />
                                  </td>
                                ) : null}
                                <td className="px-3 py-1.5" aria-hidden />
                                <td className="px-3 py-1.5 text-right tabular-nums">
                                  {formatHours(r.billable)}
                                </td>
                                <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                                  {formatHours(r.nonBillable)}
                                </td>
                                <td className="px-3 py-1.5 text-right tabular-nums">
                                  {formatHours(r.total)}
                                </td>
                                <td className="px-3 py-1.5 text-right tabular-nums text-sky-700">
                                  {(r.leaveDays || 0).toFixed(1)}
                                </td>
                                <td
                                  className={`px-3 py-1.5 text-right tabular-nums ${prodClass(rp)}`}
                                >
                                  {rp.toFixed(1)}%
                                </td>
                              </tr>
                              {ro &&
                                monthKeys.map((ym) => {
                                  const m = r.byMonth[ym];
                                  if (!m) return null;
                                  const mp =
                                    m.total > 0
                                      ? (m.billable / m.total) * 100
                                      : 0;
                                  return (
                                    <tr
                                      key={`${rk}-${ym}`}
                                      className="border-b border-slate-50/60 bg-slate-50/50 text-[12px]"
                                    >
                                      <td />
                                      <td className="px-3 py-0.5 pl-10 text-slate-500 italic">
                                        {labelYearMonthKey(ym)}
                                      </td>
                                      {projectTechColumn ? (
                                        <td className="px-3 py-0.5" aria-hidden />
                                      ) : null}
                                      <td className="px-3 py-0.5" aria-hidden />
                                      <td className="px-3 py-0.5 text-right tabular-nums text-slate-700">
                                        {m.billable.toLocaleString(undefined, {
                                          maximumFractionDigits: 2,
                                        })}
                                      </td>
                                      <td className="px-3 py-0.5 text-right tabular-nums text-slate-600">
                                        {m.nonBillable.toLocaleString(
                                          undefined,
                                          {
                                            maximumFractionDigits: 2,
                                          },
                                        )}
                                      </td>
                                      <td className="px-3 py-0.5 text-right tabular-nums text-slate-800">
                                        {m.total.toLocaleString(undefined, {
                                          maximumFractionDigits: 2,
                                        })}
                                      </td>
                                      <td className="px-3 py-0.5 text-right tabular-nums text-sky-700">
                                        {(m.leaveDays || 0).toFixed(1)}
                                      </td>
                                      <td
                                        className={`px-3 py-0.5 text-right tabular-nums ${prodClass(mp)}`}
                                      >
                                        {mp.toFixed(1)}%
                                      </td>
                                    </tr>
                                  );
                                })}
                            </React.Fragment>
                          );
                        })
                    : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {techBadgeModal ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40"
          role="presentation"
          onClick={() => setTechBadgeModal(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="tech-badge-modal-title"
            className="bg-white rounded-xl border border-slate-200 shadow-xl max-w-lg w-full max-h-[min(80vh,28rem)] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-slate-100">
              <h2
                id="tech-badge-modal-title"
                className="text-sm font-semibold text-slate-800 pr-2"
              >
                {techBadgeModal.title}
              </h2>
              <button
                type="button"
                className="p-1 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                aria-label="Close"
                onClick={() => setTechBadgeModal(null)}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-4 py-3 overflow-y-auto">
              <div className="flex flex-wrap gap-1.5">
                {techBadgeModal.labels.map((t, i) => (
                  <span
                    key={`${t}-${i}`}
                    className={techBadgeClassFor(t)}
                    title={t}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
